"use client";

import Session from "supertokens-auth-react/recipe/session";
import { useAccountStore } from "@workspace/core/accounts/store";
import { useMemorialStore } from "@/core/memorial/store";
import { useOnboardingStore } from "@/core/onboarding/store";
import { useUploadManagerStore } from "@/features/files/upload-manager-store";

export const SESSION_IDENTITY_KEY = "geooffice-session-user";
export const TAB_IDENTITY_KEY = "geooffice-tab-user";
export const SESSION_IDENTITY_EVENT = "geooffice-session-invalidated";
export const SESSION_USER_HEADER = "x-geooffice-user-id";

let boundUserId: string | null = null;
let invalidated = false;
let requests = new AbortController();

export function getSessionAbortSignal() {
  return requests.signal;
}

export function getBoundUserId() {
  return boundUserId;
}

export function getTabUserId() {
  try {
    return sessionStorage.getItem(TAB_IDENTITY_KEY);
  } catch {
    return null;
  }
}

export function isSessionInvalidated() {
  return invalidated;
}

/** Only call after checking the identity against the authenticated backend. */
export function bindSessionIdentity(userId: string) {
  if (boundUserId !== null && boundUserId !== userId) requests.abort();
  let storedUserId: string | null = null;
  try {
    storedUserId = localStorage.getItem(SESSION_IDENTITY_KEY);
  } catch {
    // Without persistence, discard user data instead of assuming its owner.
  }
  if (
    (boundUserId !== null && boundUserId !== userId) ||
    storedUserId !== userId
  ) {
    clearUserState();
  }
  if (requests.signal.aborted) requests = new AbortController();
  boundUserId = userId;
  invalidated = false;
  try {
    sessionStorage.setItem(TAB_IDENTITY_KEY, userId);
  } catch {
    /* Storage may be disabled. */
  }
  try {
    if (storedUserId !== userId) {
      localStorage.setItem(SESSION_IDENTITY_KEY, userId);
    }
  } catch {
    // The in-memory binding and backend check still apply without storage.
  }
}

export function clearUserState() {
  // Zustand updates memory before persisting. A denied/full storage must not
  // interrupt the other resets or the event that hides the old account view.
  for (const reset of [
    () => useAccountStore.getState().clearAccounts(),
    () => useMemorialStore.getState().reset(),
    () => useMemorialStore.getState().setHasHydrated(true),
    () => useOnboardingStore.getState().reset(),
    () =>
      useUploadManagerStore.setState({ activeUploads: {}, failedUploads: [] }),
  ]) {
    try {
      reset();
    } catch {
      /* The in-memory reset has already run. */
    }
  }
  for (const key of [
    "geoplatform-account-storage",
    "geoplatform-memorial-storage",
    "onboarding-data",
  ]) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* Storage may be disabled. */
    }
  }
  try {
    sessionStorage.removeItem("geooffice-new-reconstitution");
  } catch {
    /* Storage may be disabled. */
  }
  try {
    document.cookie = "account_slug=;path=/;max-age=0;SameSite=Lax";
  } catch {
    // A browser that denies cookie writes must still hide the old view.
  }
}

/** Stop old views/requests immediately; do not revoke another tab's new session. */
export function invalidateSessionIdentity(broadcast = false) {
  invalidated = true;
  requests.abort();
  clearUserState();
  if (broadcast) {
    try {
      sessionStorage.removeItem(TAB_IDENTITY_KEY);
    } catch {
      /* Storage may be disabled. */
    }
    try {
      localStorage.removeItem(SESSION_IDENTITY_KEY);
    } catch {
      /* Storage may be disabled. */
    }
  }
  window.dispatchEvent(new Event(SESSION_IDENTITY_EVENT));
}

export async function verifySessionIdentity(expectedUserId: string) {
  if (
    !(await Session.doesSessionExist()) ||
    (await Session.getUserId()) !== expectedUserId
  ) {
    throw new Error("SESSION_USER_MISMATCH");
  }
  const response = await fetch("/api/auth/session-user", {
    credentials: "include",
    cache: "no-store",
    headers: { [SESSION_USER_HEADER]: expectedUserId },
  });
  if (!response.ok) throw new Error("SESSION_IDENTITY_UNVERIFIED");
  const session = await response.json();
  if (session.userId !== expectedUserId)
    throw new Error("SESSION_USER_MISMATCH");
}
